import React, { createContext, useState, useContext, useEffect } from "react";
import axiosInstance from "../axiosInstace";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const DEBUG = false;

const logDebug = (label, data) => {
  if (DEBUG) {
    console.groupCollapsed(
      `%c[DEBUG] ${label}`,
      "color: #3b82f6; font-weight: bold;",
    );
    console.log(data);
    console.groupEnd();
  }
};

/**
 * @typedef {object} ModuleAccess
 * @property {string} moduleAccessID
 * @property {string[]} moduleFeatureAccessIDs
 */

/**
 * @typedef {object} User
 * @property {string} status
 * @property {string} employeeName
 * @property {string} username
 * @property {string[]} authorities
 * @property {string} role
 * @property {string} companyName
 * @property {ModuleAccess[]} moduleAccess
 */

/**
 * @typedef {object} AuthorizationConfig
 * @property {string[]} [roles]
 * @property {string} [module]
 * @property {string} [feature]
 * @property {boolean} [requireAll]
 */

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate(); // ✅ added
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const getStoredAuthorities = () => {
    try {
      const storedAuthorities = sessionStorage.getItem("Authorities");
      return storedAuthorities ? JSON.parse(storedAuthorities) : [];
    } catch (error) {
      console.error("Error parsing stored authorities:", error);
      return [];
    }
  };

  const getStoredRole = () => sessionStorage.getItem("Role") || "";

  const clearSessionStorage = () => {
    sessionStorage.clear();
    logDebug("Session Storage Cleared", {});
  };

  useEffect(() => {
    logDebug("Auth Provider mounted", "Starting session establishment...");

    const establishSessionOnLoad = async () => {
      try {
        const tenantId = sessionStorage.getItem("tenantId");
        const productId = process.env.REACT_APP_PRODUCT_ID;

        logDebug("Attempting to refresh token with credentials", {
          tenantId,
          productId,
        });

        const refreshTokenResponse = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL}/refresh-token`,
          null,
          {
            headers: {
              "X-TenantID": tenantId,
              "X-ProductID": productId,
            },
            withCredentials: true,
          },
        );

        const newJwtToken =
          refreshTokenResponse.headers["Authorization"] ||
          refreshTokenResponse.headers["authorization"];

        if (newJwtToken) {
          sessionStorage.setItem("jwtToken", newJwtToken);
          logDebug("New JWT Token stored in sessionStorage.", newJwtToken);
        } else {
          throw new Error("No JWT token received from refresh endpoint.");
        }

        logDebug("Token refreshed. Now fetching user access data...");

        const accessResponse = await axiosInstance.get("/api/access");

        logDebug("User Access Response", {
          status: accessResponse.status,
          data: accessResponse.data,
        });

        if (accessResponse.data) {
          const userData = {
            ...accessResponse.data,
            authorities:
              accessResponse.data.authorities || getStoredAuthorities(),
            role: accessResponse.data.role || getStoredRole(),
          };
          setUser(userData);
          logDebug("User session fully established.", userData);

          // ✅ KEY FIX: Only redirect to dashboard when coming from the login page.
          // Without this check, navigate("/dashboard") runs on every page load/refresh,
          // which causes any page to immediately redirect back to /dashboard.
          if (["/", "/login"].includes(window.location.pathname)) {
            navigate("/dashboard");
          }
        } else {
          throw new Error("API /api/access did not return any user data.");
        }
      } catch (error) {
        logDebug("Session establishment failed", { error });
        console.error("Failed to establish session:", error.message);
        setUser(null);
        sessionStorage.removeItem("jwtToken");
      } finally {
        setIsLoading(false);
        logDebug("Initial loading process finished.");
      }
    };

    // ✅ Added fallback session check (mirrors HRM pattern)
    const checkSessionFallback = async () => {
      const url = "/api/access";
      try {
        logDebug("Session Check - Request", { url, method: "GET" });
        const response = await axiosInstance.get(url);
        logDebug("Session Check - Response", {
          status: response.status,
          data: response.data,
        });

        if (response.data) {
          const userData = {
            ...response.data,
            authorities: response.data.authorities || getStoredAuthorities(),
            role: response.data.role || getStoredRole(),
          };
          setUser(userData);
          logDebug("User Loaded (fallback)", userData);
        }
      } catch (error) {
        console.error("[Auth Fallback Error]", error.message);
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    const tenantId = sessionStorage.getItem("tenantId");

    if (tenantId) {
      logDebug(
        "Tenant ID found",
        "Proceeding to establish session with token refresh.",
      );
      establishSessionOnLoad();
    } else {
      logDebug("No Tenant ID found", "Using fallback session check.");
      checkSessionFallback(); // ✅ try to restore session instead of just stopping
    }
  }, [navigate]);

  const login = (userData) => {
    const completeUserData = {
      ...userData,
      authorities: userData.authorities || getStoredAuthorities(),
      role: userData.role || getStoredRole(),
    };
    setUser(completeUserData);
    logDebug("Manual login successful", completeUserData);
  };

  const logout = async () => {
    const url = "/api/logout";
    try {
      logDebug("Logout - Request", { url, method: "POST" });
      await axiosInstance.post(url);
      logDebug("Logout Successful", {});
    } catch (error) {
      console.error("Logout Error:", error);
    } finally {
      setUser(null);
      clearSessionStorage();
      window.location.href = "/";
    }
  };

  // === ROLE-BASED ACCESS CONTROL ===
  const hasRole = (role) => user?.authorities?.includes(role) ?? false;
  const hasAuthority = (authority) =>
    user?.authorities?.includes(authority) ?? false;
  const hasAllAuthorities = (authorities) =>
    authorities.every((auth) => user?.authorities?.includes(auth));
  const hasAnyAuthority = (authorities) =>
    authorities.some((auth) => user?.authorities?.includes(auth));

  // === MODULE-BASED ACCESS CONTROL ===
  const canAccessModule = (moduleId) =>
    user?.moduleAccess?.some((module) => module.moduleAccessID === moduleId) ??
    false;

  const canAccessModuleFeature = (featureId) => {
    if (!user || !user.moduleAccess) return false;
    const allFeatureIds = user.moduleAccess.flatMap(
      (module) => module.moduleFeatureAccessIDs,
    );
    return allFeatureIds.includes(featureId);
  };

  const hasModuleAccess = (moduleId, featureId = null) => {
    if (!user || !user.moduleAccess) return false;
    const moduleEntry = user.moduleAccess.find(
      (mod) => mod.moduleAccessID === moduleId,
    );
    if (!moduleEntry) return false;
    if (!featureId) return true;
    return moduleEntry.moduleFeatureAccessIDs.includes(featureId);
  };

  // === UNIFIED AUTHORIZATION SYSTEM ===
  const isAuthorized = (config = {}) => {
    if (!user) return false;
    const { roles = [], module, feature, requireAll = false } = config;
    if (!roles.length && !module && !feature) return true;

    let roleCheck = true;
    let moduleCheck = true;

    if (roles.length > 0) {
      roleCheck = requireAll
        ? roles.every((role) => user.authorities?.includes(role))
        : roles.some((role) => user.authorities?.includes(role));
    }

    if (module || feature) {
      if (module && feature) {
        moduleCheck = hasModuleAccess(module, feature);
      } else if (feature) {
        moduleCheck = canAccessModuleFeature(feature);
      } else if (module) {
        moduleCheck = canAccessModule(module);
      }
    }

    return requireAll ? roleCheck && moduleCheck : roleCheck || moduleCheck;
  };

  // === UTILITY FUNCTIONS ===
  const getUserAuthorities = () => user?.authorities || [];
  const getUserRole = () => user?.role || "";

  const filterMenuItems = (menuItems) => {
    return menuItems.filter((item) => {
      if (item.auth) return isAuthorized(item.auth);
      if (item.roles || item.requiredModules || item.requiredFeature) {
        return isAuthorized({
          roles: item.roles,
          module: item.requiredModules?.[0],
          feature: item.requiredFeature,
        });
      }
      if (item.feature) return isAuthorized({ feature: item.feature });
      if (item.features)
        return item.features.some((feature) => isAuthorized({ feature }));
      return true;
    });
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
    hasRole,
    hasAuthority,
    hasAllAuthorities,
    hasAnyAuthority,
    canAccessModule,
    canAccessModuleFeature,
    hasModuleAccess,
    isAuthorized,
    filterMenuItems,
    getUserAuthorities,
    getUserRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
